import React from 'react';
import { motion } from 'framer-motion';
import { Shirt, Users, RefreshCw, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const benefits = [{
  icon: Shirt,
  title: 'Professional Branding',
  description: 'Consistent, high-quality apparel that represents your business'
}, {
  icon: Users,
  title: 'Team Coordination',
  description: 'Unified look for employees, crews, and team members'
}, {
  icon: RefreshCw,
  title: 'Easy Reorders',
  description: 'Scalable solutions for growing teams and repeat orders'
}];
const faqs = [{
  question: 'Do you have minimum order requirements?',
  answer: 'No minimums. Whether you need one shirt or a full team run, we support orders of any size.'
}, {
  question: 'How do reorders work?',
  answer: "We keep your artwork and specifications on file, making reorders fast and consistent. Just let us know what you need and we'll match your previous order exactly."
}, {
  question: 'What is the typical timeline for custom apparel?',
  answer: 'Most custom apparel orders are completed within 7-10 business days after artwork approval. Rush production is available for time-sensitive projects.'
}, {
  question: 'What types of businesses do you work with?',
  answer: 'We work with contractors, small businesses, racing teams, schools, events, and organizations across Michigan. Our process is built to scale from small orders to large production runs.'
}, {
  question: 'Can you help with design and artwork?',
  answer: 'Yes. We offer free design help to clean up, adjust, or create designs so your apparel looks right before production.'
}, {
  question: 'What decoration methods do you use?',
  answer: 'We use DTF transfers, screen printing, and other methods based on your specific needs. We focus on outcomes—durable, professional apparel—not just printing methods.'
}];
export function CustomApparel() {
  return <div className="min-h-screen bg-atomic-black" data-id="element-709">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-710">
        <div className="absolute inset-0 z-0" data-id="element-711">
          <img src="https://cdn.magicpatterns.com/uploads/hyy2rjmouYWqAwKEbjKg1n/Trusted_by_Teams_and_Businesses-bg.jpg" alt="Workers wearing custom Williams Farms branded hoodies" className="absolute inset-0 w-full h-full object-cover" data-id="element-712" />
          <div className="absolute inset-0 bg-atomic-black/80" data-id="element-713" />
          <div className="absolute inset-0 bg-gradient-to-b from-atomic-black/60 via-atomic-black/70 to-atomic-black/90" data-id="element-714" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-715">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-716">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-717">
              Custom Apparel for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-718">
                Businesses & Teams
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-719">
              Professional, durable apparel for businesses, teams, and
              organizations across Michigan. From one-off shirts to full
              production runs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-720">
              <a href="/contact" data-id="element-721">
                <Button size="lg" data-id="element-722">
                  Request Apparel Quote
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-723" />
                </Button>
              </a>
              <a href="/catalog" data-id="element-724">
                <Button variant="outline" size="lg" data-id="element-725">
                  View Catalog
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-726">
        <div className="container mx-auto px-4 md:px-6" data-id="element-727">
          <div className="max-w-5xl mx-auto" data-id="element-728">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-729">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-730">
                Why Choose Our Custom Apparel
              </h2>
              <p className="text-lg text-gray-300" data-id="element-731">
                Built for consistency, scalability, and professional results
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-732">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-733">
                  <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20" data-id="element-734">
                    <benefit.icon size={24} data-id="element-735" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-736">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-737">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-738">
        <div className="container mx-auto px-4 md:px-6" data-id="element-739">
          <div className="max-w-3xl mx-auto" data-id="element-740">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-741">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-742">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-743">
                Common questions about custom apparel
              </p>
            </motion.div>
            <div className="space-y-6" data-id="element-744">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10" data-id="element-745">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-746">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-747">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}